import { useState, useEffect, useCallback } from 'react'
import { Event } from '../types'
import { apiService } from '../services/api'

interface UseEventsOptions {
  eventType?: Event['eventType'] | 'all'
  timeFilter?: 'all' | 'upcoming' | 'current' | 'today'
  signupOnly?: boolean
  autoRefresh?: boolean
  refreshInterval?: number
}

interface UseEventsReturn {
  events: Event[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  signupForEvent: (eventId: string) => Promise<void>
  withdrawFromEvent: (eventId: string) => Promise<void>
  signupLoading: string | null
}

export function useEvents(options: UseEventsOptions = {}): UseEventsReturn {
  const {
    eventType,
    timeFilter = 'upcoming',
    signupOnly = false,
    autoRefresh = true,
    refreshInterval = 30000 // 30 seconds
  } = options

  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [signupLoading, setSignupLoading] = useState<string | null>(null)

  // Load events from API
  const loadEvents = useCallback(async () => {
    try {
      setError(null)

      const queryParams = new URLSearchParams()
      
      if (eventType && eventType !== 'all') {
        queryParams.append('eventType', eventType)
      }
      
      if (timeFilter === 'upcoming') {
        queryParams.append('upcoming', 'true')
      } else if (timeFilter === 'current') {
        queryParams.append('current', 'true')
      } else if (timeFilter === 'today') {
        const today = new Date().toISOString().split('T')[0]
        queryParams.append('startDate', today)
        queryParams.append('endDate', today + 'T23:59:59.999Z')
      }
      
      if (signupOnly) {
        queryParams.append('signupOnly', 'true')
      }

      const endpoint = `/events${queryParams.toString() ? '?' + queryParams.toString() : ''}`
      const response = await apiService.request<{ events: Event[]; count: number }>(endpoint)

      if (response.error) {
        throw new Error(response.error.message)
      }

      // Handle both wrapped ({ data: { events } }) and unwrapped ({ events }) response formats
      const responseData = (response.data as any)?.data || response.data
      setEvents(responseData?.events || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }, [eventType, timeFilter, signupOnly])

  // Refresh function for manual refresh
  const refresh = useCallback(async () => {
    setLoading(true)
    await loadEvents()
  }, [loadEvents])

  // Sign up for an event
  const signupForEvent = useCallback(async (eventId: string) => {
    try {
      setSignupLoading(eventId)
      const response = await apiService.signupForEvent(eventId)

      if (response.error) {
        throw new Error(response.error.message)
      }

      // Refresh events to get updated participant list
      await loadEvents()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up for event')
      throw err
    } finally {
      setSignupLoading(null)
    }
  }, [loadEvents])

  // Withdraw from an event
  const withdrawFromEvent = useCallback(async (eventId: string) => {
    try {
      setSignupLoading(eventId)
      const response = await apiService.withdrawFromEvent(eventId)

      if (response.error) {
        throw new Error(response.error.message)
      }

      // Refresh events to get updated participant list
      await loadEvents()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw from event')
      throw err
    } finally {
      setSignupLoading(null)
    }
  }, [loadEvents])

  // Initial load
  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  // Auto-refresh events
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      loadEvents()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, loadEvents])

  // Listen for real-time event updates via custom events
  useEffect(() => {
    const handleEventUpdate = (event: CustomEvent) => {
      const { type, eventData } = event.detail
      
      switch (type) {
        case 'event_created':
        case 'event_updated':
          // Refresh events to get the latest data
          loadEvents()
          break
        case 'event_deleted':
          // Remove the deleted event from local state
          setEvents(prevEvents => 
            prevEvents.filter(e => e.eventId !== eventData.eventId)
          )
          break
        default:
          break
      }
    }

    window.addEventListener('event:update', handleEventUpdate as EventListener)
    
    return () => {
      window.removeEventListener('event:update', handleEventUpdate as EventListener)
    }
  }, [loadEvents])

  // Listen for push notification events
  useEffect(() => {
    const handlePushNotification = (event: CustomEvent) => {
      const { type, data } = event.detail
      
      if (type === 'event_notification') {
        // Show browser notification if permission is granted
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(data.title, {
            body: data.message,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: `event-${data.eventId}`,
            requireInteraction: false,
            silent: false
          })
        }
        
        // Refresh events to get the latest data
        loadEvents()
      }
    }

    window.addEventListener('push:notification', handlePushNotification as EventListener)
    
    return () => {
      window.removeEventListener('push:notification', handlePushNotification as EventListener)
    }
  }, [loadEvents])

  return {
    events,
    loading,
    error,
    refresh,
    signupForEvent,
    withdrawFromEvent,
    signupLoading
  }
}

// Hook for getting current/today's events specifically
export function useCurrentEvents() {
  return useEvents({
    timeFilter: 'current',
    autoRefresh: true,
    refreshInterval: 15000 // Refresh every 15 seconds for current events
  })
}

// Hook for getting upcoming events
export function useUpcomingEvents() {
  return useEvents({
    timeFilter: 'upcoming',
    autoRefresh: true,
    refreshInterval: 60000 // Refresh every minute for upcoming events
  })
}

// Hook for getting today's events
export function useTodayEvents() {
  return useEvents({
    timeFilter: 'today',
    autoRefresh: true,
    refreshInterval: 30000 // Refresh every 30 seconds for today's events
  })
}